import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { buildCdnURL, formatPrice } from "../../../utils/utils";
import { useDispatch } from "react-redux";
import {
  removeFromCart,
  updateCartQuantity,
} from "../../../store/slices/cartSlice";
import { MinusCircle, PlusCircle, X } from "lucide-react";
import toast from "react-hot-toast";

const CartCard = ({ product, isPaused = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isCartQuantityMoreThanAvailable =
    product.quantity > product?.availableQuantity;
  const quantityInputRef = useRef(null);

  useEffect(() => {
    if (quantityInputRef.current) {
      quantityInputRef.current.value = product.quantity;
    }
  }, [product.quantity]);

  const handleQuantityChange = (productId, newQ) => {
    let newQuantity = newQ;
    if (newQuantity >= 0 || newQuantity === "") {
      toast.dismiss();
      if (newQuantity > product?.availableQuantity) {
        toast.error(
          `Only ${product?.availableQuantity} items available in stock.`
        );
        newQuantity = product?.availableQuantity || 0;
      }
      if (!newQuantity && typeof newQuantity === "number") {
        return dispatch(removeFromCart(productId));
      }
      dispatch(updateCartQuantity({ productId, quantity: newQuantity }));
    }
  };

  const handleRemoveProduct = () => {
    dispatch(removeFromCart(product.stockId));
    toast.success("Product removed from cart");
  };
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className={`flex items-center justify-between border-b py-4 last:border-b-0 relative ${
        isPaused 
          ? "border-red-500 !border-2 rounded-lg px-2 bg-red-50" 
          : "border-gray-200"
      }`}
    >
      {/* Floating cross icon for paused products */}
      {isPaused && (
        <button
          onClick={handleRemoveProduct}
          className="absolute top-2 right-2 z-10 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors shadow-md"
          aria-label="Remove product"
        >
          <X className="w-4 h-4" />
        </button>
      )}
      <div className="flex items-center">
        <img
          src={buildCdnURL(product.images?.[0]) || "/assets/placeholder.webp"}
          alt={product.name}
          className="w-16 h-16 object-cover rounded-md mr-4"
          onClick={() => navigate(`/product/${product?.stockId}`)}
        />
        <div>
          <h3 className={`font-semibold ${isPaused ? "text-red-600" : ""}`}>
            {product.name}
          </h3>
          <h2>{product?.variant?.title}</h2>
          <p className="text-sm text-gray-500">{product.description}</p>
          {isPaused && (
            <p className="text-sm text-red-600 font-medium mt-1">
              ⚠️ This product is not available. Please remove it from cart.
            </p>
          )}
          <div className="flex items-center mt-1 flex-wrap-reverse gap-1">
            <p className="font-semibold">
              ₹{formatPrice(product?.sellingPrice * product.quantity)}
            </p>
            <p className="text-sm text-gray-600">
              ( {product.quantity} x ₹{formatPrice(product.sellingPrice)} )
            </p>
            {/* Show original price with strikethrough if discounted */}
            {product.price && product.price > product.sellingPrice && (
              <p className="text-sm text-gray-400 line-through ml-2">
                ₹{formatPrice(product.price * product.quantity)}
              </p>
            )}
          </div>
        </div>
      </div>
      <div
        className={`flex items-center border rounded-full ${
          isPaused 
            ? "border-red-300 bg-red-50" 
            : isCartQuantityMoreThanAvailable 
              ? "border-red-500" 
              : "border-gray-300"
        }`}
      >
        <button
          onClick={() =>
            handleQuantityChange(product.stockId, product.quantity - 1)
          }
          className="p-1 text-gray-600 hover:text-primary-100 transition-colors"
        >
          <MinusCircle className="w-4 h-4" />
        </button>
        <input
          ref={quantityInputRef}
          type="number"
          onBlur={(e) => {
            const newQuantity = Number.parseInt(e.target.value, 10);
            handleQuantityChange(product.stockId, newQuantity || 0);
          }}
          className={`w-12 text-center font-semibold border-none focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            isPaused ? "bg-red-50" : ""
          }`}
          min="0"
        />
        <button
          onClick={() =>
            handleQuantityChange(product.stockId, product.quantity + 1)
          }
          className="p-1 text-gray-600 hover:text-primary-100 transition-colors"
        >
          <PlusCircle className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};

export default CartCard;
